/**
 * TypeScript Interfaces for Reference Persons (AI Avatar) Feature
 *
 * This file contains all type definitions for the AI Avatar feature,
 * which allows users to upload reference photos for character-consistent
 * image generation in Instagram reel scenes.
 */

import type { Id } from '../../convex/_generated/dataModel';

// ============================================================================
// Core Types
// ============================================================================

/**
 * Reference Person document interface (mirrors Convex schema)
 */
export interface IReferencePerson {
  _id: Id<'referencePersons'>;
  _creationTime: number;
  userId: Id<'users'>;
  name: string;
  description?: string;
  imageIds: Id<'_storage'>[];
  imageUrls: string[]; // Resolved URLs from storage
  isDefault?: boolean; // User-selected default person
  isDeleted: boolean;
  deletedAt?: number;
  createdAt: number;
  updatedAt: number;
}

/**
 * Form input for creating/editing a reference person
 */
export interface IReferencePersonFormInput {
  name: string;
  description?: string;
}

// ============================================================================
// Component Props
// ============================================================================

/**
 * Reference Person List Props
 */
export interface IReferencePersonListProps {
  persons: IReferencePerson[];
  isLoading: boolean;
  onEdit: (personId: Id<'referencePersons'>) => void;
  onDelete: (personId: Id<'referencePersons'>) => void;
  onViewImages: (personId: Id<'referencePersons'>) => void;
  onSetDefault: (personId: Id<'referencePersons'>) => void;
  onAddPerson: () => void;
  canAddPerson: boolean;
}

/**
 * Reference Person Card Props
 */
export interface IReferencePersonCardProps {
  person: IReferencePerson;
  onEdit: () => void;
  onDelete: () => void;
  onViewImages: () => void;
  onSetDefault: () => void;
  maxImages: number;
}

/**
 * Add Person Card Props
 */
export interface IAddPersonCardProps {
  onClick: () => void;
  disabled?: boolean;
}

/**
 * Reference Person Form Props
 */
export interface IReferencePersonFormProps {
  initialValues?: IReferencePersonFormInput;
  onSubmit: (values: IReferencePersonFormInput) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  mode: 'create' | 'edit';
}

/**
 * Reference Image Upload Props
 */
export interface IReferenceImageUploadProps {
  personId: Id<'referencePersons'>;
  currentImages: Array<{ id: Id<'_storage'>; url: string }>;
  maxImages: number;
  onImageAdded: (storageId: Id<'_storage'>) => void;
  onImageRemoved: (storageId: Id<'_storage'>) => void;
  disabled?: boolean;
}

/**
 * Delete Person Dialog Props
 */
export interface IDeletePersonDialogProps {
  personName: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
}

// ============================================================================
// Hook Return Types
// ============================================================================

/**
 * useReferencePersons hook return type
 */
export interface IUseReferencePersonsReturn {
  // Data
  persons: IReferencePerson[];
  personCount: number;

  // Loading states
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isUploadingImage: boolean;

  // Error state
  error: string | null;

  // Actions
  createPerson: (
    input: IReferencePersonFormInput
  ) => Promise<{ success: boolean; personId?: Id<'referencePersons'>; error?: string }>;
  updatePerson: (
    personId: Id<'referencePersons'>,
    input: Partial<IReferencePersonFormInput>
  ) => Promise<{ success: boolean; error?: string }>;
  deletePerson: (personId: Id<'referencePersons'>) => Promise<{ success: boolean; error?: string }>;
  setDefault: (personId: Id<'referencePersons'>) => Promise<{ success: boolean; error?: string }>;
  addImage: (
    personId: Id<'referencePersons'>,
    file: File
  ) => Promise<{ success: boolean; storageId?: Id<'_storage'>; error?: string }>;
  removeImage: (
    personId: Id<'referencePersons'>,
    storageId: Id<'_storage'>
  ) => Promise<{ success: boolean; error?: string }>;

  // Computed
  canCreatePerson: boolean;
  maxPersons: number;
  maxImagesPerPerson: number;
}

// ============================================================================
// Constants
// ============================================================================

export const REFERENCE_PERSON_CONSTANTS = {
  MAX_PERSONS_PER_USER: 5,
  MAX_IMAGES_PER_PERSON: 14,
  MAX_NAME_LENGTH: 50,
  MAX_DESCRIPTION_LENGTH: 500,
  MAX_IMAGE_SIZE_MB: 5,
  ACCEPTED_IMAGE_TYPES: ['image/jpeg', 'image/png'] as const,
} as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format image count display (e.g., "3/5 images")
 */
export function formatImageCount(count: number, max: number): string {
  return `${count}/${max} images`;
}

/**
 * Validate image file for upload
 * Checks file type and size against constants
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  const { MAX_IMAGE_SIZE_MB, ACCEPTED_IMAGE_TYPES } = REFERENCE_PERSON_CONSTANTS;

  // Check file type
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type as (typeof ACCEPTED_IMAGE_TYPES)[number])) {
    return { valid: false, error: 'Only JPEG and PNG images are accepted' };
  }

  // Check file size
  if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
    return { valid: false, error: `Image must be less than ${MAX_IMAGE_SIZE_MB}MB` };
  }

  return { valid: true };
}

/**
 * Check if a person is active (has required number of images)
 */
export function isPersonActive(imageCount: number, maxImages: number): boolean {
  return imageCount >= maxImages;
}
